import { useLatest } from '@solinkcorp/solink-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type BaseStore } from 'stores/BaseStore';

/**
 * React hook which returns some data from a flux store.
 *
 * @param isEqual - By default, `useFluxStore()` will update state if the value
 *   returned by selector is not equal to the old value in an `===` sense.  If
 *   `isEqual` is provided, this is used to test for equality instead.
 * @example
 *
 *    const location = useFluxStore(LocationStore, (store) => {
 *         return store.getLocation(locationId);
 *    });
 *
 */
export default function useFluxStore<T extends typeof BaseStore, S>(
  store: T,
  selector: (store: T) => S,
  isEqual?: (oldState: S, newState: S) => boolean,
) {
  const latestIsEqual = useLatest(isEqual);

  // Note that we have both a `latestSelectedState` and a `useState` for the
  // state - technically we don't need both, but when we call `setState()` it
  // affects the 'next' render, so by doing things this way we can shave an
  // unnecessary render off every compontent that uses this.  We also keep
  // track of the `latestSelector` to minimize the number of times we need
  // to call `selector()`.
  const latestSelector = useRef<(store: T) => S>(selector);
  const lastestSelectedState = useRef<S>(selector(store));
  const [, setState] = useState<S | undefined>();

  const checkIsEqual = useCallback(
    (oldState: S, newState: S) => {
      return latestIsEqual.current
        ? latestIsEqual.current(oldState, newState)
        : oldState === newState;
    },
    [latestIsEqual],
  );

  // If the selector changes, update the state.
  if (selector !== latestSelector.current) {
    latestSelector.current = selector;
    const newState = selector(store);
    if (!checkIsEqual(lastestSelectedState.current, newState)) {
      lastestSelectedState.current = selector(store);
      // Don't setState here - we're already re-rendering, no need to do it again.
    }
  }

  // Subscribe to changes in the store.
  useEffect(() => {
    const listener = () => {
      const newState = latestSelector.current(store);
      if (!checkIsEqual(lastestSelectedState.current, newState)) {
        lastestSelectedState.current = newState;
        // Call `setState()` to force a re-render.
        setState(newState);
      }
    };

    store.addChangeListener(listener);
    return () => store.removeChangeListener(listener);
  }, [store, setState, checkIsEqual]);

  return lastestSelectedState.current;
}
